import { getCurrentScope, onScopeDispose, ref, watch } from 'vue'
import axiosInstance from '@/axios'
import {
    COLORWAY_PENDING_RETRY_MS,
    COLORWAY_STATE,
} from '@/utils/previewColorway'

/**
 * useArtifiPreview — composable for fetching rendered Artifi preview URLs
 * for a given (variantSku, viewCode, artworkFileId) tuple.
 *
 * Boulders card "Product editor (hi-fi redesign)", pebble 13 PR 2 - the
 * editor-side caller for Artifi's GetImage API. The canonical Product
 * Editor Stage.vue (via useArtifiPreviewCache) is the primary consumer
 * today.
 *
 * Public surface (locked contract):
 *
 *   const { url, loading, error, warnings, refresh } = useArtifiPreview(argsRef)
 *
 *   argsRef:  ref<{ variantSku, viewCode, artworkFileId } | null>
 *   url:      ref<string|null>           — rendered preview URL
 *   loading:  ref<boolean>               — request in flight or debouncing
 *   error:    ref<{code,message}|null>   — backend error payload (artifi_render_error,
 *                                          artifi_disabled, etc.) or transport error
 *   notFound: ref<boolean>               — true iff the backend returned 404
 *                                          sku_not_found (issue #2202): the
 *                                          EXPECTED "Artifi does not recognize
 *                                          this SKU" state. Distinct from a
 *                                          fault so the UI can show a friendly
 *                                          message; not retried into a burst
 *                                          (a 404 fires exactly one request).
 *   warnings: ref<string[]>              — non-fatal warnings from the bridge
 *                                          (e.g. ['bare-product'])
 *   colorwayState: ref<string|null>      — the server's `colorway.state` for
 *                                          this render: 'applied' (the bound
 *                                          colorway is on the image),
 *                                          'pending' (being created: the
 *                                          image is the default colors and
 *                                          this composable asks again, a few
 *                                          times, on COLORWAY_PENDING_RETRY_MS),
 *                                          'unavailable' (could not be
 *                                          created: default colors, no
 *                                          retry), or null when no colorway
 *                                          applies to the request.
 *   refresh:  () => void                 — force a re-fetch with the last
 *                                          dispatched args (e.g. Refresh
 *                                          / Retry buttons on the
 *                                          canonical Stage.vue). No-op
 *                                          when there's no prior
 *                                          dispatch.
 *
 * Backend contract (GET /api/artifi/preview, Track B):
 *   200 → { url, warnings, cached, colorway?: { state } }
 *   400 → missing/invalid params
 *   401 → anon access denied
 *   502 → { error: { code: 'artifi_render_error', message } }
 *   503 → { error: { code: 'artifi_disabled', message } }
 *
 * Behavior notes:
 *   - 250ms debounce on args changes so rapid edits (zoom slider, color
 *     swap, etc. in pebble 14) coalesce into one render request.
 *   - Module-level in-flight de-dup keyed on (sku|view|fileId): two
 *     composable instances watching the same args produce ONE axios
 *     call, and both see the resolved url. Critical for the editor +
 *     preview card sharing a render.
 *   - Args change cancels the prior request via key supersession — a
 *     late stale response is silently discarded.
 *   - Incomplete args (null / missing field) clears state and does not
 *     fire a request.
 */
const DEBOUNCE_MS = 250
const ENDPOINT = '/api/artifi/preview'
// /gate Finding P2 #12 — explicit request timeout so a hung connection
// can't pin an in-flight Map entry forever (which would block all future
// requests for the same key).
//
// 120s, not 15s (2026-10-08): a render can legitimately take longer than
// 15s -- a color clicked in "Colors on this product" right after it was
// ticked waits on the server until the save's eager every-color render
// pass finishes (17-20s measured on 8088). Aborting never cancels the
// server's render, so a short timeout freed nothing and only threw away a
// render that was already paid for, leaving the Stage blank. Kept under
// the API's gunicorn worker timeout (180s), past which the server itself
// gives up.
const REQUEST_TIMEOUT_MS = 120_000

// Module-level shared in-flight map. Keyed by canonical
// `${sku}|${viewCode}|${fileId}` so two composables watching the same
// tuple await one promise. Entries clear when the request settles.
const inflight = new Map()

function canonicalKey(args) {
    // Day 3 (audit gap #4): artworkFileId may be null on the
    // bare-render path. Coerce to a stable empty string in the key so
    // two bare-render callers for the same (sku, view) share an
    // in-flight entry.
    //
    // 2026-06-19 Option C: include the resolution params so two
    // bare-render callers with DIFFERENT master_sku/color_name don't
    // share an in-flight entry (they resolve to different upstream
    // SKUs). When omitted (legacy callers), the empty-string coerce
    // preserves the existing de-dup behaviour.
    // Multi-placement composite (card https://trello.com/c/pI48s0zf):
    // campaignProductId, when present, is the composite render's identity
    // (all placements on the active view's group). Include it so two
    // composites on different campaign-products don't share an in-flight
    // entry, and a composite never de-dups against a single-artwork call.
    return [
        args.variantSku,
        args.viewCode,
        args.artworkFileId ?? '',
        args.masterSku ?? '',
        args.colorName ?? '',
        args.campaignProductId ?? '',
        // ADR 0065 amendment: ``source`` tags the consumer surface for meter
        // attribution + the server-side budget gate. Fold into the key so a
        // decorations_page call and an untagged (money-path) call for the same
        // tuple do not share an in-flight entry -- their server-side gating
        // differs, so they must not de-dup against each other.
        args.source ?? '',
        // The colorway a placement sews changes the rendered pixels, so the
        // placement and its bound colorway are part of this request's
        // identity. The server reads the binding when the request ARRIVES: a
        // rebind that overlapped an earlier request for the same placement
        // must send its own, or it would join that request and be handed the
        // previous colorway's image. `colorwayId` is identity only (never on
        // the wire); `placementId` is the wire parameter the server derives
        // the colorway from.
        args.placementId ?? '',
        args.colorwayId ?? '',
    ].join('|')
}

// Exported so useArtifiPreviewCache asks the SAME question before it marks
// itself loading: args this composable will never fetch for must not leave
// the wrapper loading forever.
export function argsComplete(args) {
    // Day 3 (audit gap #4): artworkFileId is now optional —
    // when absent, the composable fires the bare-render path so the
    // backend returns a garment-only preview (no logo). variantSku +
    // viewCode remain required so the backend knows which product +
    // view to render.
    return (
        !!args &&
        typeof args.variantSku === 'string' &&
        args.variantSku.length > 0 &&
        typeof args.viewCode === 'string' &&
        args.viewCode.length > 0
    )
}

export const NO_PLACEMENTS_ERROR_CODE = 'no_placements'

// The one surface that previews an artwork on a garment with NO product
// behind it (the Decoration Library page, GarmentRenderLayer). It is gated
// by its own 90% budget control instead.
const PRODUCTLESS_SOURCES = new Set(['decorations_page'])

/**
 * The placement gate (Jake, 2026-10-07): "NEVER call Artifi unless the
 * product has at least one placement." It lives HERE, at the one function
 * every Artifi preview request passes through, so no caller can forget it.
 * A product-scoped request carries `placementCount`; it is allowed only when
 * that is at least 1, and a request that does not say is refused (fail
 * closed -- a new caller that omits it renders nothing rather than spends).
 *
 * @param {object} args
 * @returns {boolean}
 */
export function placementGateAllows(args) {
    if (PRODUCTLESS_SOURCES.has(args?.source)) return true
    return Number.isInteger(args?.placementCount) && args.placementCount > 0
}

/**
 * Fire (or join) the preview request for one args tuple, outside any
 * component state. This is the SAME request the composable sends -- same
 * endpoint, same wire params, same module-level in-flight de-dup -- exposed
 * so a caller that renders MANY tuples (the storefront listing's per-color
 * renders, useStorefrontColorRenders) does not need a second render path or
 * one reactive composable instance per color. A Stage render and a listing
 * render for the same tuple share one HTTP call.
 *
 * Resolves with the axios response ({ data: { url, warnings, cached } });
 * rejects with the axios error. Callers own their own caching
 * (artifiPreviewCache) and loading state.
 *
 * @param {object} args - the useArtifiPreview args shape.
 * @returns {Promise<object>}
 */
export function requestArtifiPreview(args) {
    if (!placementGateAllows(args)) {
        const err = new Error(
            'No Artifi preview for a product without a placement',
        )
        err.code = NO_PLACEMENTS_ERROR_CODE
        err.response = {
            data: { error: { code: NO_PLACEMENTS_ERROR_CODE, message: err.message } },
        }
        return Promise.reject(err)
    }
    const key = canonicalKey(args)
    let promise = inflight.get(key)
    if (!promise) {
        const params = {
            variant_sku: args.variantSku,
            view_code: args.viewCode,
        }
        // Multi-placement composite (card https://trello.com/c/pI48s0zf):
        // when campaignProductId is present the backend composites EVERY
        // placement on the active view's group (ThreadFlow-side per-view
        // render + PIL merge). The composite is derived from placements, so
        // we do NOT send artwork_file_id (which would trigger the single-
        // artwork path).
        if (args.campaignProductId != null) {
            params.campaign_product_id = args.campaignProductId
        } else if (args.artworkFileId) {
            // Day 3 (audit gap #4): omit artwork_file_id entirely when
            // null/empty so the backend takes the bare-render path.
            // Sending `artwork_file_id=null` as a string would fail the
            // endpoint's format regex.
            params.artwork_file_id = args.artworkFileId
        }
        // 2026-06-19 Option C: variant SKU resolution wire params.
        // When the editor knows the SanMar master SKU + color label,
        // forward them so the backend can resolve to an Artifi
        // numeric variant SKU (handles CSV reverse lookup cases like
        // "Graphite Heather" -> "GphHeather" that the client-side
        // coverage map can't easily match).
        if (args.masterSku) {
            params.master_sku = args.masterSku
        }
        if (args.colorName) {
            params.color_name = args.colorName
        }
        // ADR 0065 amendment: forward the consumer tag so the backend
        // attributes meter burn to this surface AND applies the 90%
        // budget gate to the Decorations page (source=decorations_page).
        if (args.source) {
            params.source = args.source
        }
        // The placement being previewed (single-artwork path only). The
        // SERVER derives the colorway from it (#4721, D1): it routes the
        // garment color to the variant that applies and composites that
        // variant's recolored asset. The client never names a colorway, so a
        // caller cannot ask for another placement's. Omitted when no
        // placement is named (bare render, composite, staged placement),
        // which keeps today's exact request.
        if (args.campaignProductId == null && args.placementId != null) {
            params.placement_id = args.placementId
        }
        promise = axiosInstance
            .get(ENDPOINT, {
                params,
                timeout: REQUEST_TIMEOUT_MS,
            })
            .finally(() => {
                // Clear ourselves from the map so future calls with
                // the same key issue a fresh request rather than
                // re-resolving against stale data.
                if (inflight.get(key) === promise) {
                    inflight.delete(key)
                }
            })
        inflight.set(key, promise)
    }
    return promise
}

export function useArtifiPreview(argsRef) {
    const url = ref(null)
    const loading = ref(false)
    const error = ref(null)
    const notFound = ref(false)
    const warnings = ref([])
    const colorwayState = ref(null)

    let debounceTimer = null
    // The pending-colorway re-request timer (see dispatch).
    let retryTimer = null
    // The key this instance is currently waiting on; used to discard
    // late responses when args change mid-flight.
    let currentKey = null
    // Snapshot of the last args we dispatched for — used by ``refresh()``
    // to re-fire the same request when the user clicks Refresh / Retry
    // with otherwise-unchanged args. Bar-raiser P1 #3 on pebble 14:
    // without this, the watcher won't fire on identical args and the
    // buttons are no-ops.
    let lastArgs = null

    function clearRetryTimer() {
        if (retryTimer) {
            clearTimeout(retryTimer)
            retryTimer = null
        }
    }

    function reset() {
        clearRetryTimer()
        colorwayState.value = null
        url.value = null
        loading.value = false
        error.value = null
        notFound.value = false
        warnings.value = []
    }

    async function dispatch(args, key, attempt = 0) {
        // Reuse a shared in-flight promise if another caller already
        // fired the same request. Otherwise create one and register it.
        const promise = requestArtifiPreview(args)

        try {
            const response = await promise
            // Discard if this instance's args changed while waiting.
            if (currentKey !== key) return
            // State before url: a consumer watching url reads it.
            const state = response.data?.colorway?.state ?? null
            colorwayState.value = state
            url.value = response.data?.url ?? null
            warnings.value = response.data?.warnings ?? []
            error.value = null
            notFound.value = false
            // `pending`: the image is the default colors while the bound
            // colorway's asset is created. Show it, then ask again after a
            // short, bounded delay. Never a tight loop, and never more than
            // COLORWAY_PENDING_RETRY_MS.length extra requests; whether any of
            // them may spend is the server's decision.
            if (
                state === COLORWAY_STATE.PENDING &&
                attempt < COLORWAY_PENDING_RETRY_MS.length
            ) {
                clearRetryTimer()
                retryTimer = setTimeout(() => {
                    retryTimer = null
                    if (currentKey !== key) return
                    dispatch(args, key, attempt + 1)
                }, COLORWAY_PENDING_RETRY_MS[attempt])
            }
        } catch (err) {
            if (currentKey !== key) return
            // A failed RE-request keeps the image already on screen: the
            // first answer was good, only the poll failed.
            if (attempt > 0) return
            colorwayState.value = null
            url.value = null
            warnings.value = []
            const errPayload =
                err?.response?.data?.error ?? {
                    code: 'unknown',
                    message: err?.message ?? 'Preview request failed',
                }
            error.value = errPayload
            // Issue #2202: a 404 sku_not_found is the expected "Artifi does
            // not recognize this SKU" state -- flag it so the UI shows a
            // friendly message instead of a render-error banner. The single
            // request already fired; the catch does not retry, so a bad SKU
            // cannot burst.
            notFound.value = errPayload.code === 'sku_not_found'
        } finally {
            if (currentKey === key) {
                loading.value = false
            }
        }
    }

    function scheduleFetch(args) {
        if (debounceTimer) {
            clearTimeout(debounceTimer)
            debounceTimer = null
        }
        // A pending re-request belongs to the args it was scheduled for.
        clearRetryTimer()

        if (!argsComplete(args)) {
            currentKey = null
            reset()
            return
        }

        const key = canonicalKey(args)
        currentKey = key
        lastArgs = args
        // The previous request's state describes the previous args. Clearing
        // it makes every answer a CHANGE a consumer's watcher sees: an
        // applied -> applied rebind would otherwise fire nothing.
        colorwayState.value = null
        loading.value = true
        error.value = null
        notFound.value = false

        debounceTimer = setTimeout(() => {
            debounceTimer = null
            // Re-check that args haven't been superseded during the
            // debounce window.
            if (currentKey !== key) return
            dispatch(args, key)
        }, DEBOUNCE_MS)
    }

    /**
     * Force a re-fetch with the last dispatched args. Used by the
     * Refresh / Retry buttons on the canonical Stage.vue (bar-raiser
     * P1 #3 on pebble 14, surfaced via useArtifiPreviewCache) where
     * args haven't changed but the user wants to retry an errored
     * render or re-pull from Artifi.
     *
     * Behavior:
     *   - No-op when no prior dispatch has happened (no lastArgs).
     *   - Otherwise: clears any in-flight entry for the current key
     *     (so we don't dedupe against the request we're retrying),
     *     clears error state, and re-runs the same dispatch path
     *     (including the debounce window) for the same args.
     */
    function refresh() {
        if (!lastArgs) return
        const key = canonicalKey(lastArgs)
        // Clear any in-flight entry so the next dispatch issues a fresh
        // request rather than awaiting the old (possibly errored)
        // promise. Without this, two errored callers would both await
        // the rejected promise and see the same failure.
        if (inflight.has(key)) {
            inflight.delete(key)
        }
        // Re-enter the normal scheduleFetch path so we benefit from
        // debounce / loading / error state management.
        scheduleFetch(lastArgs)
    }

    watch(
        argsRef,
        (newArgs) => {
            // Snapshot to insulate against later reactive mutation of
            // the same object reference.
            const snapshot = newArgs
                ? {
                      variantSku: newArgs.variantSku,
                      viewCode: newArgs.viewCode,
                      artworkFileId: newArgs.artworkFileId,
                      // 2026-06-19 Option C resolution params.
                      masterSku: newArgs.masterSku,
                      colorName: newArgs.colorName,
                      // Multi-placement composite identity.
                      campaignProductId: newArgs.campaignProductId,
                      // ADR 0065 amendment: consumer tag (budget gate).
                      source: newArgs.source,
                      // Placement + colorway identity. This snapshot is an
                      // explicit allow-list -- a field absent here is
                      // silently dropped before it ever reaches the wire
                      // params or the in-flight key.
                      placementId: newArgs.placementId,
                      colorwayId: newArgs.colorwayId,
                      // The placement gate's input (requestArtifiPreview).
                      placementCount: newArgs.placementCount,
                  }
                : null
            scheduleFetch(snapshot)
        },
        { immediate: true, deep: true },
    )

    // Timers outlive the component unless cleared. (Tests and one-off
    // callers may run outside any effect scope.)
    if (getCurrentScope()) {
        onScopeDispose(() => {
            if (debounceTimer) clearTimeout(debounceTimer)
            clearRetryTimer()
            currentKey = null
        })
    }

    return { url, loading, error, notFound, warnings, colorwayState, refresh }
}

export default useArtifiPreview
